'use client';

import { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useI18n } from '@/i18n';
import { authApi } from '@/lib/api';
import { Modal } from './ui';

function Spinner({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

const codeInputCls =
  'w-full px-3.5 py-2.5 rounded-lg border border-border bg-input text-foreground text-base tracking-[0.35em] text-center font-mono placeholder:text-faint outline-none transition-all focus:ring-2 focus:ring-maple/30 focus:border-maple';

export default function TwoFaModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  // 打开时拉取状态；未启用则同时获取绑定参数
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const st = await authApi.twoFaStatus();
      setEnabled(st.enabled);
      if (!st.enabled) {
        const s = await authApi.twoFaSetup();
        setSetup(s);
      } else {
        setSetup(null);
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setCode('');
    setCopied(false);
    setError('');
    setSuccess('');
    setEnabled(null);
    setSetup(null);
    void load();
  }, [open, load]);

  const handleCopy = async () => {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed');
    }
  };

  const handleConfirm = async () => {
    if (!setup) return;
    if (!/^\d{6}$/.test(code)) {
      setError(t('login.codeError'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authApi.twoFaConfirm({ secret: setup.secret, code });
      setCode('');
      setSuccess(t('twofa.bindSuccess'));
      // 绑定成功后短暂提示并自动关闭，不切换到解绑视图
      window.setTimeout(onClose, 1200);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError(t('login.codeError'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authApi.twoFaDisable({ code });
      setCode('');
      setSuccess(t('twofa.unbindSuccess'));
      // 刷新状态，未启用时重新拉取绑定参数，便于直接再次绑定
      const st = await authApi.twoFaStatus();
      setEnabled(st.enabled);
      if (!st.enabled) {
        const s = await authApi.twoFaSetup();
        setSetup(s);
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  /* ---- 绑定视图 ---- */
  const bindView = setup && (
    <div className="space-y-5">
      <div className="flex items-center gap-2.5">
        <svg className="w-5 h-5 text-maple flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
        <h4 className="text-base font-semibold text-foreground">{t('twofa.bindTitle')}</h4>
      </div>

      <p className="text-sm text-muted">{t('twofa.scanHint')}</p>

      <div className="flex justify-center">
        <div className="bg-white p-3 rounded-xl shadow-sm">
          <QRCodeSVG value={setup.otpauthUrl} size={180} />
        </div>
      </div>

      <div className="rounded-xl bg-bg2 border border-border p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-secondary">{t('twofa.manualHint')}</p>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-hover text-secondary hover:bg-border transition-colors cursor-pointer"
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5 text-badge-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('twofa.copied')}
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('twofa.copy')}
              </>
            )}
          </button>
        </div>
        <div className="mt-2 font-mono text-sm text-foreground break-all tracking-wide">{setup.secret}</div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">{t('twofa.enterCode')}</label>
        <input
          type="text"
          inputMode="numeric"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className={codeInputCls}
          placeholder="000000"
          maxLength={6}
        />
      </div>

      <button
        onClick={handleConfirm}
        disabled={loading}
        className="w-full py-2.5 rounded-lg bg-gradient-to-r from-maple to-maple-light text-neutral-900 font-semibold shadow-lg shadow-maple/20 hover:brightness-105 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {loading ? <Spinner /> : t('twofa.confirmBind')}
      </button>
    </div>
  );

  /* ---- 已启用视图 ---- */
  const unbindView = (
    <div className="space-y-5">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-badge-success-bg border border-badge-success-border">
        <svg className="w-5 h-5 text-badge-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <span className="text-sm font-semibold text-badge-success">{t('twofa.bound')}</span>
      </div>

      <div className="border-t border-border pt-5 space-y-4">
        <p className="text-sm text-muted">{t('twofa.unbindHint')}</p>
        <input
          type="text"
          inputMode="numeric"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className={codeInputCls}
          placeholder="000000"
          maxLength={6}
        />
        <button
          onClick={handleDisable}
          disabled={loading}
          className="w-full py-2.5 rounded-lg border border-badge-danger-border bg-badge-danger-bg text-badge-danger font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {loading ? <Spinner /> : t('twofa.unbind')}
        </button>
      </div>
    </div>
  );

  let content;
  if (enabled === null) {
    content = (
      <div className="flex items-center justify-center py-16">
        {error ? <p className="text-sm text-badge-danger">{error}</p> : <Spinner className="w-8 h-8 text-maple" />}
      </div>
    );
  } else if (enabled) {
    content = (
      <div className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-badge-danger-bg border border-badge-danger-border text-badge-danger text-sm">{error}</div>
        )}
        {success && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-badge-success-bg border border-badge-success-border text-badge-success text-sm">{success}</div>
        )}
        {unbindView}
      </div>
    );
  } else if (setup) {
    content = (
      <div className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-badge-danger-bg border border-badge-danger-border text-badge-danger text-sm">{error}</div>
        )}
        {success && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-badge-success-bg border border-badge-success-border text-badge-success text-sm">{success}</div>
        )}
        {bindView}
      </div>
    );
  } else {
    content = (
      <div className="flex items-center justify-center py-16">
        {error ? <p className="text-sm text-badge-danger">{error}</p> : <Spinner className="w-8 h-8 text-maple" />}
      </div>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={t('twofa.title')}>
      {content}
    </Modal>
  );
}
